import * as Validator from 'validatorjs';
import { isNotBlank } from './utils';

Validator.registerImplicit(
  'required_if_contains',
  function (val) {
    const [fieldName, searchStr] = this.getParameters() ?? [];

    const fieldValue = this.validator._objectPath(
      this.validator.input,
      fieldName
    );

    if (fieldValue.includes(searchStr)) {
      return this.validator.getRule('required').validate(val);
    }

    return true;
  },
  ''
);

Validator.registerImplicit(
  'required_if_and',
  function (val) {
    const parameters = this.getParameters();
    let result = true;

    for (let i = 0; i < parameters.length - 1; i += 2) {
      const fieldName = parameters[i];
      const searchStr = [...parameters[i + 1]];

      const fieldValue = this.validator._objectPath(
        this.validator.input,
        fieldName
      );

      result = result && searchStr.includes(fieldValue);
    }

    if (result) {
      return this.validator.getRule('required').validate(val);
    }

    return true;
  },
  ''
);

Validator.registerImplicit(
  'required_if_one_of',
  function (val) {
    const [fieldName, ...values] = this.getParameters() ?? [];

    const fieldValue = this.validator._objectPath(
      this.validator.input,
      fieldName
    );

    if (values.some(value => value == fieldValue)) {
      return this.validator.getRule('required').validate(val);
    }

    return true;
  },
  ''
);

Validator.registerImplicit(
  'required_if_min_value',
  function (val) {
    const [fieldName, ...values] = this.getParameters() ?? [];

    const fieldValue = this.validator._objectPath(
      this.validator.input,
      fieldName
    );
    if (values.every(value => fieldValue >= value)) {
      return this.validator.getRule('required').validate(val);
    }
    return true;
  },
  ''
);

Validator.registerImplicit(
  'required_if_true',
  function (val) {
    const fieldNames = this.getParameters() ?? [];

    const fieldValues = fieldNames.map(fieldName =>
      this.validator._objectPath(this.validator.input, fieldName)
    );

    if (fieldValues.every(value => value === true)) {
      return this.validator.getRule('required').validate(val);
    }

    return true;
  },
  ''
);

Validator.registerImplicit(
  'valid_address',
  function (val) {
    const addressFields = this.getParameters() ?? [];

    const fieldValues = addressFields.map(fieldName =>
      this.validator._objectPath(this.validator.input, fieldName)
    );

    return fieldValues.every(value => isNotBlank(value));
  },
  'Invalid address'
);

Validator.registerImplicit(
  'address',
  function (val) {
    const isValid = val?.address && val?.city && val?.state && val?.zip;
    return isValid;
  },
  'Please fill all address-related fields'
);

Validator.registerImplicit('address_state_not_in', function (val) {
  const invalidValues = this.getParameters() ?? [];
  const state = val?.state || '';
  return !invalidValues.includes(state);
});

/**
 * This validator would take in rules and process them one by one
*/
Validator.registerImplicit(
  'combined_validation',
  function (val) {
    const context = this;
    const nestedRules = this.getParameters(); // Get the array of nested rules
    return validateAllNested(nestedRules, val, context);
  },
  ''
);

function validateAllNested(nestedRules, value, context) {
  for (const rule of nestedRules) {
    const ruleName = Object.keys(rule)[0];
    const ruleParams = rule[ruleName];

    if (context.validator.getRule(ruleName).validate(value, ruleParams, context.attribute, null)) {
      return true;
    }
  }
  return false;
}

Validator.registerImplicit(
  'non_negative',
  function (val) {
    if (val === null || val === undefined || val === '') {
      return true;
    }

    return val.toString().length > 0 && val > 0;
  },
  'Loan ID cannot be negative'
);


Validator.register('valid_date', (value) => {
  if (!isNaN(value) && value instanceof Date) {
    return true;
  }

  const dateFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if (dateFormatRegex.test(value)) {
    return true;
  }

  return false;
}, 'Invalid date format or value.');

Validator.registerImplicit(
  'required_unless_either',
  function (value, requirement) {
    const data = this.validator.input;
    const [field1, value1, field2, value2] = requirement;
    if (data[field1] === value1 || data[field2] === value2) {
      return true;
    }

    if (value !== undefined && value !== null && value.toString().trim() !== '') {
      return true;
    }

    return false;
  },
  'The :attribute field is required.'
);


/*
Usage: { required_if_email_format: ['fieldName', true, 'Yes'] }
Params: conditional field + values that trigger validation
Behavior: required AND must be a valid email if the other field matches any value
*/

Validator.registerImplicit(
  'required_if_email_format',
  function (val) {
    const [fieldName, ...expectedValues] = this.getParameters() ?? [];

    const fieldValue = this.validator._objectPath(
      this.validator.input,
      fieldName
    );

    const expectedVals = expectedValues.map(v => v.toString());
    const normalizedFieldVal = fieldValue?.toString();

    const shouldBeRequired = expectedVals.includes(normalizedFieldVal);

    if (shouldBeRequired) {
      const isFilled = this.validator.getRule('required').validate(val);
      if (!isFilled) return false;

      return this.validator.getRule('email').validate(val);
    }

    return true;
  },
  'You must enter a valid email address'
);

/*
Usage: { required_if_phone_format: ['fieldName', true, 'Yes'] }
Params: conditional field + values that trigger validation
Behavior: required AND must match phone format xxx-xxx-xxxx if the other field matches any value
*/
Validator.registerImplicit(
  'required_if_phone_format',
  function (val) {
    const parameters = this.getParameters();
    const fieldName = parameters[0];
    const expectedValues = parameters.slice(1);

    const fieldValue = this.validator._objectPath(this.validator.input, fieldName);
    const normalized = fieldValue?.toString();

    if (expectedValues.some(v => v.toString() === normalized)) {
      if (!val || val.toString().trim() === '') return false;

      const phoneRegex = /^[2-9]\d{2}-\d{3}-\d{4}$/;
      return phoneRegex.test(val.toString());
    }

    return true;
  },
  'Phone must be in the format xxx-xxx-xxxx'
);
